import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { BedDouble, Plus, Trash2 } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const pad = (n) => String(n).padStart(2, "0");
const today = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const STATUTS = { present: ["Présent", "#16a34a"], hospitalise: ["Hospitalisé", "#dc2626"], absent: ["Absent", "#ea580c"], sortie: ["Sortie", "#d97706"], exterieur: ["Repas ext.", "#0284c7"] };

export default function Habitants() {
  const { t } = useTranslation();
  const [st, setSt] = useState(null);
  const [form, setForm] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/cuisine/repas/state", { params: { date: today(), service: "midi" } }); setSt(data); } catch { }
  }, []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    if (!form.room.trim()) return;
    try {
      await api.post("/cuisine/repas/rooms", { room: form.room.trim().toUpperCase(), floor: Number(form.floor) || 0, civilite: form.civilite, status: form.status, prefs: form.prefs || { texture: "normal", liquides: "0" }, scope: "permanent", service: "midi", date: today() });
      toast.success(t("hab.saved")); setForm(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const remove = async () => {
    if (!window.confirm(t("hab.deleteConfirm"))) return;
    try { await api.delete(`/cuisine/repas/rooms/${encodeURIComponent(form.room)}`); toast.success(t("hab.deleted")); setForm(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  if (!st) return <div className="p-8 text-muted-foreground">…</div>;
  const rooms = st.rooms || [];
  return (
    <div className="space-y-5 p-4 sm:p-6" data-testid="habitants">
      <div className="rounded-3xl p-6 glass flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-semibold tracking-[0.2em] uppercase text-muted-foreground">{st.maison}</div>
          <h1 className="font-display text-3xl font-extrabold flex items-center gap-2"><BedDouble className="h-7 w-7 text-teal-400" /> {t("nav.habitants")}</h1>
          <p className="text-sm text-muted-foreground">{t("hab.subtitle")} — {rooms.length} {t("bd.room").toLowerCase()}s</p>
        </div>
        {st.can_edit_permanent && <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full" data-testid="hab-add" onClick={() => setForm({ room: "", floor: 0, civilite: "", status: "present" })}><Plus className="h-4 w-4 mr-1.5" /> {t("hab.addRoom")}</Button>}
      </div>
      {[0, 1, 2, 3, 4, 5].map((f) => {
        const list = rooms.filter((r) => r.floor === f);
        if (!list.length) return null;
        return (
          <div key={f}>
            <h2 className="font-display font-bold text-teal-400 uppercase text-sm mb-2">{f === 0 ? "Rez-de-chaussée" : `${f}${f === 1 ? "er" : "e"} étage`} <span className="text-muted-foreground">({list.length})</span></h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-2">
              {list.map((r) => {
                const [lbl, col] = STATUTS[r.status] || STATUTS.present;
                return (
                  <button key={r.room} data-testid={`hab-${r.room}`} onClick={() => st.can_edit_permanent && setForm({ room: r.room, floor: r.floor, civilite: r.civilite, status: r.status, prefs: r.prefs, existing: true })}
                    className="rounded-2xl bg-white text-slate-800 p-3 text-center shadow hover:shadow-lg hover:-translate-y-0.5 transition">
                    <div className="text-xl font-extrabold">{r.room}</div>
                    <div className="text-[10px] text-slate-400">{r.civilite || "—"} · {t("hab.namePen")}</div>
                    <div className="mt-1 text-[9px] font-extrabold text-white rounded-full px-2 py-0.5 inline-block" style={{ background: col }}>{lbl}</div>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-sm">
          {form && <>
            <DialogHeader><DialogTitle>{t("hab.addRoom")}</DialogTitle></DialogHeader>
            <p className="text-xs text-amber-500 font-semibold">🖊 {t("hab.rgpd")}</p>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">{t("bd.room")} (ex. 101G)</Label><Input data-testid="hab-form-room" value={form.room} onChange={(e) => setForm({ ...form, room: e.target.value })} /></div>
              <div><Label className="text-xs">{t("bd.floor")}</Label><Input type="number" min="0" value={form.floor} onChange={(e) => setForm({ ...form, floor: e.target.value })} /></div>
              <div><Label className="text-xs">M./Mme</Label>
                <select className="w-full h-10 rounded-md border px-2 bg-background" value={form.civilite} onChange={(e) => setForm({ ...form, civilite: e.target.value })}><option value=""></option><option>M.</option><option>Mme</option></select></div>
              <div><Label className="text-xs">{t("bd.status")}</Label>
                <select className="w-full h-10 rounded-md border px-2 bg-background" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>{Object.entries(STATUTS).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}</select></div>
            </div>
            <DialogFooter>
              {form.existing && <Button variant="destructive" className="mr-auto" data-testid="hab-delete" onClick={remove}><Trash2 className="h-4 w-4 mr-1.5" /> {t("hab.deleteRoom")}</Button>}
              <Button variant="ghost" onClick={() => setForm(null)}>{t("planning.cancel")}</Button>
              <Button className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="hab-save" onClick={save} disabled={!form.room}>{t("inv.save")}</Button>
            </DialogFooter>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
